import type { LapTrace } from './laps'
import { sampleAt } from './laps'
import type { TrackDef } from '../lib/tracks'

/** A stretch of the lap, in metres along the reference lap. */
export interface Section {
  index: number
  name: string
  from: number
  to: number
}

/** How far (m) a boundary point may sit from the reference lap's line before its metre mark is used instead. */
const MARK_TOLERANCE_M = 60

/**
 * Lay the track's sections over the reference lap. A boundary given as a GPS point is placed
 * where the reference lap passes it (so the split is the same spot on the track whatever the
 * line); a metre mark is used as is. The last section always runs to the end of the lap, and
 * empty sections are dropped.
 */
export function resolveSections(def: TrackDef | undefined, ref: LapTrace | number): Section[] {
  const lapLength = typeof ref === 'number' ? ref : ref.distance[ref.distance.length - 1]
  if (!def || !(lapLength > 0)) return []
  const locate = typeof ref === 'number' ? null : markLocator(ref)
  const out: Section[] = []
  let from = 0
  for (let i = 0; i < def.sections.length; i++) {
    const s = def.sections[i]
    const last = i === def.sections.length - 1
    let to: number | undefined = last ? lapLength : undefined
    if (to == null && s.at && locate) to = locate(s.at.lat, s.at.lon, from)
    if (to == null && s.to != null) to = s.to
    // A boundary that cannot be placed (the lap never passes it, no metre mark) is left out:
    // its section merges into the next one.
    if (to == null) continue
    to = Math.min(to, lapLength)
    if (to > from) out.push({ index: out.length, name: s.name, from, to })
    if (to >= lapLength) break
    from = Math.max(from, to)
  }
  return out
}

/**
 * Finds where the lap passes a point: the nearest spot on its GPS line, searched forward from
 * the previous boundary so a track that crosses near itself is not confused, as the distance
 * along the lap. Undefined when the lap never comes within MARK_TOLERANCE_M of the point.
 */
function markLocator(ref: LapTrace): (lat: number, lon: number, after: number) => number | undefined {
  const gps = ref.gps
  if (gps.length < 2) return () => undefined
  const lat0 = gps[0].lat
  const lon0 = gps[0].lon
  const mLat = 111_320
  const mLon = 111_320 * Math.cos((lat0 * Math.PI) / 180)
  const n = gps.length
  const xs = new Float64Array(n)
  const ys = new Float64Array(n)
  const ds = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    xs[i] = (gps[i].lon - lon0) * mLon
    ys[i] = (gps[i].lat - lat0) * mLat
    ds[i] = sampleAt(ref.time, ref.distance, gps[i].time - ref.lap.start)
  }
  return (lat, lon, after) => {
    const px = (lon - lon0) * mLon
    const py = (lat - lat0) * mLat
    let best = Infinity
    let bestD: number | undefined
    for (let i = 0; i < n - 1; i++) {
      if (ds[i + 1] <= after) continue
      const ax = xs[i]
      const ay = ys[i]
      const dx = xs[i + 1] - ax
      const dy = ys[i + 1] - ay
      const len2 = dx * dx + dy * dy
      let f = len2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0
      f = Math.max(0, Math.min(1, f))
      const d = Math.hypot(px - (ax + f * dx), py - (ay + f * dy))
      if (d < best) {
        best = d
        bestD = ds[i] + f * (ds[i + 1] - ds[i])
      }
    }
    return best <= MARK_TOLERANCE_M && bestD != null && bestD > after ? bestD : undefined
  }
}

/** Index of the section containing distance `d`, or -1. */
export function sectionAt(sections: Section[], d: number | null | undefined): number {
  if (d == null || !Number.isFinite(d)) return -1
  for (const s of sections)
    if (d >= s.from && (d < s.to || (d === s.to && s.index === sections.length - 1))) return s.index
  return -1
}

export interface SectionStats {
  section: Section
  /** Seconds spent in the section */
  time: number
  /** Lowest speed in the section, in the channel's unit; NaN without a speed channel */
  minSpeed: number
  /** Distance (m from the lap start) of that lowest speed */
  minSpeedAt: number
  /** Speed when entering and leaving the section */
  entrySpeed: number
  exitSpeed: number
  /** Largest lean to the left (degrees, positive) and to the right; NaN without a lean channel */
  leanLeft: number
  leanRight: number
}

export interface StatChannels {
  speed?: string
  lean?: string
}

/** A section counts as having a corner on a side when the lean there reaches this angle. */
export const CORNER_LEAN_DEG = 10

export function sectionStats(trace: LapTrace, sections: Section[], names: StatChannels): SectionStats[] {
  const speed = names.speed ? trace.channels[names.speed] : undefined
  const lean = names.lean ? trace.channels[names.lean] : undefined
  return sections.map((section) => {
    const t0 = sampleAt(trace.distance, trace.time, section.from)
    const t1 = sampleAt(trace.distance, trace.time, section.to)
    let minSpeed = Infinity
    let minSpeedAt = NaN
    let leanLeft = 0
    let leanRight = 0
    for (let k = 0; k < trace.distance.length; k++) {
      const d = trace.distance[k]
      if (d < section.from) continue
      if (d > section.to) break
      if (speed && speed[k] < minSpeed) {
        minSpeed = speed[k]
        minSpeedAt = d
      }
      if (lean) {
        const v = lean[k]
        if (v > leanRight) leanRight = v
        if (-v > leanLeft) leanLeft = -v
      }
    }
    return {
      section,
      time: t1 - t0,
      minSpeed: speed && Number.isFinite(minSpeed) ? minSpeed : NaN,
      minSpeedAt: speed && Number.isFinite(minSpeed) ? minSpeedAt : NaN,
      entrySpeed: speed ? sampleAt(trace.distance, speed, section.from) : NaN,
      exitSpeed: speed ? sampleAt(trace.distance, speed, section.to) : NaN,
      leanLeft: lean ? leanLeft : NaN,
      leanRight: lean ? leanRight : NaN,
    }
  })
}
